import { useLazyQuery } from '@apollo/client';
import React, { useEffect, useState, useRef } from 'react';
import { GET_PRODUCT_WITH_PAGINATION } from '../../apollo/gql/query/products.query';
import { CLOUD_FRONT_URL } from '../../aws.config';
import { ProductFilterInput } from '../../pages/Home/Products';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DEFAULT_PRODUCT_STATUS } from '../../lib/constants/filters';
import { navigatedProductAtom, addNewCustomFabricAtom, alankaramDataAtom, searchQueryAtom } from '../../state';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

interface SearchHintProps {
    onSearchSelect?: (query: string) => void;
    inputRef?: React.RefObject<HTMLInputElement>;
}

interface Product {
    _id: string;
    product: {
        _id: string;
        name: string;
        thumb: string;
    };
}

const SearchHint: React.FC<SearchHintProps> = ({ onSearchSelect, inputRef }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const alankaramData = useRecoilValue(alankaramDataAtom);
    const navigate = useNavigate()
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [navigatedProduct, setNavigatedProduct] = useRecoilState(navigatedProductAtom)
    const setSearchQueryAtom = useSetRecoilState(searchQueryAtom);
    const location = useLocation();
    const [searchParams,] = useSearchParams()
    const selectedCategory = searchParams.get('category')

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const initialSearch = params.get("search");

        if (initialSearch && !hasUserTyped) {
            setSearchQuery(initialSearch);
        }
    }, [location.search]);


    const [fetchProducts, { data, loading }] = useLazyQuery(GET_PRODUCT_WITH_PAGINATION, {
        fetchPolicy: "network-only",
    });

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        setHasUserTyped(true);

        if (value.trim().length === 0) {
            const params = new URLSearchParams(location.search);
            if (params.has("search")) {
                params.delete("search");
                setSearchQueryAtom('');
                navigate({ search: params.toString() }, { replace: true });
            }
        }
    };


    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            const params = new URLSearchParams(location.search);
            const paramSearch = params.get("search") || "";


            if (
                searchQuery.length > 0 &&
                (hasUserTyped || paramSearch !== searchQuery)
            ) {
                const dynamicInput: ProductFilterInput = {
                    name: [searchQuery],
                    companyId: alankaramData?.companyId || "",
                    imageTags: [],
                    category: "",
                    materialCategory: [],
                    status: "",
                    offset: 0,
                    limit: 20,
                    sort: "latest",
                    withNoStyle: true,
                    productType: [],
                    createdBy: [],
                    reviewedBy: [],
                    productListEditStatus: "all",
                    productSegmentStatus: "all",
                    productCategories: [],
                    storeCategory: [],
                    storeSubCategory: [],
                    folders: [],
                    productStatus: alankaramData?.companyId === "63e0c85267e6ab00082da07e" ? "" : DEFAULT_PRODUCT_STATUS,
                };

                fetchProducts({ variables: dynamicInput });
                setShowSuggestions(true);
            }
        }, 300);

        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [searchQuery, alankaramData?.companyId]);



    const [addNewCustomFabric, setAddNewCustomFabric] = useRecoilState(addNewCustomFabricAtom)

    const handleSuggestionClick = (product: Product) => {
        setSearchQuery(product.product.name);
        setShowSuggestions(false);
        if (onSearchSelect) {
            onSearchSelect(product.product.name);
        }
        setAddNewCustomFabric([]);
        if (product?.product) {
            setNavigatedProduct(product.product)
            navigate(`/products/product-detail/${product.product?._id}`)
        }
    };

    const filteredProducts: any = data?.getProductWithPagination || [];

    return (
        <div className={`flex-1 max-w-lg transition-all duration-200 ${isSearchFocused ? 'sm:max-w-xl' : ''}`}>
            <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <img src="/search.png" className="search-icon" alt="search" />
                </div>
                <input
                    type="text"
                    ref={inputRef}
                    placeholder={`Search your products from ${selectedCategory ?? 'All Products'}`}
                    className="w-full pl-10 pr-4 py-2 border-0 bg-gray-100 focus:ring-0 focus:ring-gray-300 focus:outline-none text-sm transition-all duration-200"
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => {
                        setIsSearchFocused(false);
                        setTimeout(() => setShowSuggestions(false), 1000);
                    }}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            const trimmed = searchQuery.trim();
                            if (trimmed) {
                                const params = new URLSearchParams(location.search);
                                params.set('search', trimmed);

                                setSearchQueryAtom(trimmed);
                                navigate({ search: params.toString() }, { replace: true });
                                setShowSuggestions(false);
                            }
                        }
                    }}


                />

                {showSuggestions && (
                    <div className="absolute w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto z-20">
                        {loading ? (
                            <div className="p-4 text-sm text-gray-500">Loading...</div>
                        ) : filteredProducts?.length > 0 ? (
                            filteredProducts?.map((product: Product,index:number) => (
                                <div className='flex gap-x-2 hover:bg-gray-100 cursor-pointer justify-start items-center px-2' key={index}>
                                    <div className="max-w-[40px] w-full h-[40px] md:w-12 md:h-12">
                                        <img src={`${CLOUD_FRONT_URL}${product?.product?.thumb}`} alt='Product' className="w-full h-full object-contain rounded-md" />
                                    </div>
                                    <div
                                        key={product?._id}
                                        className="p-2 text-sm flex-grow"
                                        onClick={() => handleSuggestionClick(product)}
                                    >
                                        {product?.product?.name}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-sm text-gray-500">
                                No products found for "<strong>{searchQuery}</strong>"
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default SearchHint; 